import { BILLING_WARN_PERCENT_OPTIONS, } from './schemas.js';
/**
 * Pure billing policy (doc 42). No I/O, no clocks of its own, no vendors —
 * imported by the API, the gateway and the portal, so the three can never
 * disagree about what a rule means.
 */
export const SECONDS_PER_MINUTE = 60;
/** Seconds a NEW call may draw on: posted balance minus active reservations. */
export function availableSeconds(s) {
    return Math.max(0, s.balanceSeconds - s.reservedSeconds);
}
/** Owner-facing split of a signed balance (doc 42 §4.1). */
export function splitBalance(balanceSeconds) {
    if (balanceSeconds < 0)
        return { minutes: 0, seconds: 0, debtSeconds: -balanceSeconds };
    return {
        minutes: Math.floor(balanceSeconds / SECONDS_PER_MINUTE),
        seconds: balanceSeconds % SECONDS_PER_MINUTE,
        debtSeconds: 0,
    };
}
/**
 * A paid top-up opens a new cycle. Baseline = what is left AFTER the credit and
 * every debit already posted — carry-over included. 20 left + 100 bought makes
 * a baseline of 120, so the next 50 % / 20 % warnings land at 60 and 24.
 */
export function startWarningCycle(balanceAfterSeconds, cycleId) {
    return {
        warningCycleId: cycleId,
        warningBaselineSeconds: Math.max(0, balanceAfterSeconds),
        warnedSlots: [],
    };
}
/**
 * Which warnings a POSTED debit owes (doc 42 §6, rules 3–8).
 *
 * - Evaluated on the posted balance, never on reservations, so reserving and
 *   releasing a call's window can never produce a false SMS.
 * - Integer comparison: `100 × remaining ≤ percent × baseline`.
 * - Each slot fires at most once per cycle; the claim id does not include the
 *   percentage, so editing the threshold never re-arms a slot already sent.
 * - Both crossed at once → both claimed, ONE message.
 * - A disabled slot (or a disabled master switch) claims nothing, so enabling
 *   it later lets the next debit send one warning if already below.
 */
export function lowBalanceWarnings(prefs, cycle, balanceSeconds) {
    const none = { claim: [], send: false };
    if (!prefs.enabled)
        return none;
    if (!cycle.warningCycleId || cycle.warningBaselineSeconds <= 0)
        return none;
    const remaining = Math.max(0, balanceSeconds);
    const claim = [];
    for (const slot of ['first', 'second']) {
        const rule = prefs[slot];
        if (!rule.enabled || cycle.warnedSlots.includes(slot))
            continue;
        if (100 * remaining <= rule.remainingPercent * cycle.warningBaselineSeconds)
            claim.push(slot);
    }
    return { claim, send: claim.length > 0 };
}
/** The threshold an owner would see for a slot, in whole minutes. */
export function warningThresholdSeconds(baselineSeconds, percent) {
    return Math.floor((baselineSeconds * percent) / 100);
}
/**
 * Validate a billing-SMS form. Used by the portal (inline errors) and by the
 * callable (authoritative). `customPhoneE164` is the server-normalised number,
 * or null when it does not normalise.
 */
export function validateBillingSmsSettings(s, ctx) {
    const errors = {};
    const opts = BILLING_WARN_PERCENT_OPTIONS;
    if (!opts.includes(s.first.remainingPercent))
        errors.first = 'percent_invalid';
    if (!opts.includes(s.second.remainingPercent))
        errors.second = 'percent_invalid';
    if (s.first.enabled &&
        s.second.enabled &&
        !errors.first &&
        !errors.second &&
        s.first.remainingPercent <= s.second.remainingPercent) {
        errors.second = 'first_must_exceed_second';
    }
    if (s.enabled && s.recipientMode === 'custom') {
        if (!s.customPhone.trim())
            errors.customPhone = 'phone_required';
        else if (!ctx.customPhoneE164)
            errors.customPhone = 'phone_invalid';
    }
    else if (s.recipientMode === 'custom' && s.customPhone.trim() && !ctx.customPhoneE164) {
        errors.customPhone = 'phone_invalid';
    }
    if (s.enabled && s.recipientMode === 'business_number' && !ctx.businessNumber) {
        errors.recipient = 'business_number_missing';
    }
    return errors;
}
/** Validate an auto top-up form against the package's CURRENT gross price. */
export function validateAutoTopup(s, ctx) {
    const errors = {};
    if (!s.enabled)
        return errors;
    if (ctx.packageSeconds === null || ctx.packageGrossMinor === null) {
        errors.packageId = 'package_unknown';
        return errors;
    }
    if (s.triggerSeconds <= 0 || s.triggerSeconds >= ctx.packageSeconds)
        errors.triggerSeconds = 'threshold_invalid';
    if (s.monthlyGrossCapMinor === null)
        errors.monthlyGrossCapMinor = 'cap_required';
    else if (s.monthlyGrossCapMinor < ctx.packageGrossMinor)
        errors.monthlyGrossCapMinor = 'cap_below_one_package';
    if (s.maxGrossPerPurchaseMinor === null || s.maxGrossPerPurchaseMinor < ctx.packageGrossMinor) {
        errors.maxGrossPerPurchaseMinor = 'per_purchase_below_price';
    }
    return errors;
}
/** A stable fingerprint of what the owner consented to (doc 42 §9). */
export function autoTopupConfigHash(s, termsVersion) {
    return [
        termsVersion,
        s.packageId ?? '-',
        s.triggerSeconds,
        s.monthlyGrossCapMinor ?? '-',
        s.maxGrossPerPurchaseMinor ?? '-',
    ].join('|');
}
// ── Entitlement (doc 42 §11) ─────────────────────────────────────────────────
/** Grace after a failed platform renewal before new calls stop (§11). */
export const PAST_DUE_GRACE_MS = 72 * 3600_000;
/**
 * Is the platform fee paid up for a prepaid business?
 *
 * The grace deadline is measured from the FIRST failure we recorded
 * (`pastDueSince`), never re-derived from a webhook's arrival, so redelivered
 * events cannot slide it. `trialing` is entitled to service — but a trial never
 * creates purchased minutes; it still needs funds like anyone else.
 */
export function prepaidEntitlement(sub, now) {
    const kind = subscriptionKind(sub);
    if (sub.status === 'none' || sub.status === 'canceled')
        return { kind: 'blocked', reason: 'no_subscription' };
    if (kind === 'unknown')
        return { kind: 'blocked', reason: 'unknown_price' };
    if (sub.status === 'past_due') {
        const since = sub.pastDueSince ?? null;
        // No recorded start is the conservative case: grace from now would let a
        // missing field grant an open-ended window, so treat it as already started.
        if (!since)
            return { kind: 'blocked', reason: 'past_due_expired' };
        const deadline = new Date(since.getTime() + PAST_DUE_GRACE_MS);
        return deadline.getTime() > now.getTime() ? { kind: 'grace', deadline } : { kind: 'blocked', reason: 'past_due_expired' };
    }
    return { kind: 'ok' };
}
/** What a subscription's price is, tolerating records written before `kind`. */
export function subscriptionKind(sub) {
    if (sub.kind)
        return sub.kind;
    if (sub.status === 'none')
        return 'none';
    return sub.plan ? 'legacy_plan' : 'unknown';
}
// ── Monthly cap (doc 42 §9) ──────────────────────────────────────────────────
/** `YYYY-MM` of `now` in `timeZone` — the cap's calendar month. */
export function billingPeriodKey(now, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit' }).formatToParts(now);
    const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
    const m = parts.find((p) => p.type === 'month')?.value ?? '01';
    return `${y}-${m}`;
}
/** `spent + pending + new ≤ cap`, all gross minor units. */
export function capAllows(p, newGrossMinor, capMinor) {
    return p.grossSpentMinor + p.grossPendingMinor + newGrossMinor <= capMinor;
}
// ── Auto top-up decision (doc 42 §9) ─────────────────────────────────────────
export const AUTO_TOPUP_COOLDOWN_MS = 60_000;
export function autoTopupDecision(i) {
    const skip = (reason) => ({ kind: 'skip', reason });
    if (!i.settings.enabled)
        return skip('disabled');
    if (i.killSwitch)
        return skip('kill_switch');
    if (!i.entitled)
        return skip('not_entitled');
    if (!i.consent)
        return skip('no_consent');
    if (i.consent.revision !== i.settings.revision || i.consent.configHash !== i.currentConfigHash)
        return skip('consent_stale');
    if (!i.paymentMethodId)
        return skip('no_payment_method');
    if (i.hold)
        return skip('hold');
    if (i.activeOrderId)
        return skip('order_active');
    if (i.available > i.settings.triggerSeconds)
        return skip('above_threshold');
    if (i.lastAutoOrderAt && i.now.getTime() - i.lastAutoOrderAt.getTime() < AUTO_TOPUP_COOLDOWN_MS)
        return skip('cooldown');
    if (i.settings.maxGrossPerPurchaseMinor === null || i.packageGrossMinor > i.settings.maxGrossPerPurchaseMinor) {
        return skip('price_review_required');
    }
    if (i.settings.monthlyGrossCapMinor === null || !capAllows(i.period, i.packageGrossMinor, i.settings.monthlyGrossCapMinor)) {
        return skip('cap_reached');
    }
    return { kind: 'order' };
}
/** Manual invoices stay payable this long, then are voided (doc 42 §9). */
export const MANUAL_ORDER_TTL_MS = 30 * 60_000;
/** Off-session network attempts per auto order, and their spacing (§9). */
export const AUTO_RETRY_DELAYS_MS = [60_000, 5 * 60_000, 15 * 60_000];
//# sourceMappingURL=policy.js.map